import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, StarHalf, Heart, ShoppingBag, Zap, ArrowRight, Truck, ShieldCheck, RotateCcw, BadgePercent, Sparkles } from "lucide-react";
import { fmtINR } from "../lib/api";
import { discountPct } from "../lib/cart";

/* Layout variants per theme: each e-commerce theme gets a distinct identity. */
export const VARIANTS = {
  market: { header: "solid", hero: "split" },
  editorial: { header: "glass", hero: "fullbleed" },
  tech: { header: "dark", hero: "split" },
  clean: { header: "glass", hero: "center" },
  bold: { header: "solid", hero: "fullbleed" },
};

const FONT_CSS = {
  "Playfair Display": "'Playfair Display', serif", "Fraunces": "'Fraunces', serif",
  "DM Serif Display": "'DM Serif Display', serif", "Lora": "'Lora', serif",
  "Sora": "'Sora', sans-serif", "Space Grotesk": "'Space Grotesk', sans-serif",
  "Outfit": "'Outfit', sans-serif", "Libre Franklin": "'Libre Franklin', sans-serif",
  "Cabinet Grotesk": "'Cabinet Grotesk', sans-serif", "Manrope": "'Manrope', sans-serif",
};

export const themeVars = (colors = {}, theme = {}) => ({
  "--sp": colors.primary || "#111111",
  "--ss": colors.secondary || "#F4F4F5",
  "--sa": colors.accent || "#C9A96E",
  "--sbg": colors.bg || "#FFFFFF",
  "--st": colors.text || "#111111",
  "--shfont": FONT_CSS[theme.font] || FONT_CSS["Cabinet Grotesk"],
  "--sbfont": FONT_CSS[theme.body_font] || FONT_CSS["Manrope"],
});

export const headingFont = () => "fh";

export const SafeImg = ({ src, alt = "", className = "", style = {} }) => {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return <div className={className} style={{ ...style, background: "linear-gradient(135deg, var(--sp), var(--sa))" }} aria-label={alt} />;
  }
  return <img src={src} alt={alt} loading="lazy" className={className} style={style} onError={() => setErr(true)} />;
};

export const Stars = ({ rating = 4.5 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${rating} stars`}>
    {[1, 2, 3, 4, 5].map((i) =>
      rating >= i ? <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
        : rating >= i - 0.5 ? <StarHalf key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
        : <Star key={i} className="h-3.5 w-3.5 text-zinc-300" />
    )}
  </span>
);

export const SectionHead = ({ overline, title, center = false }) => (
  <div className={center ? "text-center" : ""}>
    <p className="uppercase text-[11px] tracking-[0.25em] font-extrabold" style={{ color: "var(--sa)" }}>{overline}</p>
    <h2 className="fh text-3xl sm:text-4xl tracking-tighter mt-2" style={{ color: "var(--sp)" }}>{title}</h2>
  </div>
);

export const ProductCard = ({ p, onOpen, onAdd, onBuy, wished, onWish, radius = "10px", delay = 0 }) => {
  const pct = discountPct(p);
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
      transition={{ delay, duration: 0.45 }} data-testid={`product-card-${p.id}`}
      className="group bg-white border border-black/5 hover:shadow-2xl hover:shadow-black/10 hover:-translate-y-1.5 transition-all duration-300 overflow-hidden"
      style={{ borderRadius: radius }}>
      <div className="relative aspect-square overflow-hidden bg-zinc-100 cursor-pointer" onClick={onOpen}>
        <SafeImg src={p.images?.[0]} alt={p.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
        {pct > 0 && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-extrabold text-white shadow-lg"
            style={{ backgroundColor: "var(--sa)" }} data-testid={`discount-${p.id}`}>-{pct}%</span>
        )}
        <motion.button whileTap={{ scale: 1.4 }} data-testid={`wishlist-${p.id}`}
          onClick={(e) => { e.stopPropagation(); onWish(); }}
          className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow hover:scale-110 transition-transform">
          <Heart className={`h-4 w-4 transition-colors ${wished ? "fill-red-500 text-red-500" : "text-zinc-400"}`} />
        </motion.button>
        <div className="absolute inset-x-3 bottom-3 opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          <button data-testid={`quick-view-${p.id}`} onClick={(e) => { e.stopPropagation(); onOpen(); }}
            className="w-full py-2.5 rounded-lg bg-black/80 backdrop-blur text-white text-xs font-bold hover:bg-black transition-colors">
            Quick View
          </button>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-1.5"><Stars rating={p.rating ?? 4.5} /><span className="text-[10px] text-zinc-400 font-semibold">({p.reviews_count ?? 128})</span></div>
        <p className="font-bold text-sm mt-1.5 truncate cursor-pointer hover:underline underline-offset-2" style={{ color: "var(--st)" }} onClick={onOpen}>{p.name}</p>
        <div className="flex items-baseline gap-2 mt-1.5">
          <span className="font-display font-extrabold text-lg" style={{ color: "var(--sp)" }}>{fmtINR(p.price)}</span>
          {pct > 0 && <span className="text-xs line-through text-zinc-400">{fmtINR(p.compare_price)}</span>}
        </div>
        <div className="flex gap-2 mt-3">
          {p.stock <= 0 ? (
            <span className="text-xs font-bold text-red-500 py-2">Out of stock</span>
          ) : (
            <>
              <button data-testid={`add-to-cart-${p.id}`} onClick={onAdd}
                className="flex-1 py-2.5 text-xs font-bold border-2 transition-all active:scale-95 flex items-center justify-center gap-1.5 hover:bg-black/5"
                style={{ borderColor: "var(--sp)", color: "var(--sp)", borderRadius: radius }}>
                <ShoppingBag className="h-3.5 w-3.5" /> Add
              </button>
              <button data-testid={`buy-now-${p.id}`} onClick={onBuy}
                className="flex-1 py-2.5 text-xs font-bold text-white transition-all active:scale-95 hover:opacity-90 flex items-center justify-center gap-1.5 shadow-md"
                style={{ backgroundColor: "var(--sp)", borderRadius: radius }}>
                <Zap className="h-3.5 w-3.5" /> Buy Now
              </button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

/* ================= Category-specific HERO layouts ================= */
const goTo = (id, fb) => (document.getElementById(id) || (fb ? document.getElementById(fb) : null))?.scrollIntoView({ behavior: "smooth" });

export const StoreHero = ({ theme, store, info, products = [], head, radius = "10px", onShopNow, onSecondary }) => {
  const hero = theme?.hero || {};
  const layout = hero.layout || "split";
  const headline = hero.headline || store.name;
  const subline = hero.subline || info.description || "";
  const cta1 = hero.cta1 || "Shop Now";
  const cta2 = hero.cta2 || "Explore";
  const badge = hero.badge;
  const heroSrc = (hero && typeof hero === "object" && hero.image) || (typeof theme?.hero === "string" ? theme.hero : null) || theme?.hero_img || theme?.preview;
  const cats = [...new Set(products.map((p) => p.category))];
  const [slide, setSlide] = useState(0);
  const slides = [heroSrc, ...products.slice(0, 3).map((p) => p.images?.[0])].filter(Boolean);
  useEffect(() => {
    if (layout !== "carousel" || slides.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % slides.length), 3500);
    return () => clearInterval(t);
  }, [layout, slides.length]);

  const shopNow = () => (onShopNow ? onShopNow() : goTo("products", "site-content"));
  const secondary = () => (onSecondary ? onSecondary() : goTo("deals", "site-contact"));

  const Badge = badge ? (
    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-widest"
      style={{ backgroundColor: "var(--ss)", color: "var(--sp)" }}>
      <Sparkles className="h-3.5 w-3.5" /> {badge}
    </span>
  ) : null;

  const CTAs = ({ dark = false }) => (
    <div className="flex flex-wrap gap-3 mt-7">
      <button data-testid="hero-shop-now" onClick={shopNow}
        className={`px-8 py-3.5 rounded-full font-bold text-sm transition-all active:scale-95 shadow-lg hover:scale-105 duration-300 ${dark ? "bg-white text-zinc-900" : "text-white"}`}
        style={dark ? {} : { backgroundColor: "var(--sp)" }}>
        {cta1} <ArrowRight className="inline h-4 w-4 ml-1" />
      </button>
      <button data-testid="hero-secondary" onClick={secondary}
        className={`px-8 py-3.5 rounded-full font-bold text-sm border-2 transition-all active:scale-95 ${dark ? "border-white/60 text-white hover:bg-white/10" : "hover:bg-black/5"}`}
        style={dark ? {} : { borderColor: "var(--sp)", color: "var(--sp)" }}>
        {cta2}
      </button>
    </div>
  );

  if (layout === "fullbleed") return (
    <section className="relative overflow-hidden" data-testid="hero-fullbleed">
      <div className="absolute inset-0">
        <SafeImg src={heroSrc} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(100deg, ${theme?.colors?.primary || "#111"}E8 25%, ${theme?.colors?.primary || "#111"}66 60%, transparent)` }} />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-28 sm:py-40">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          {badge && <p className="uppercase text-[11px] tracking-[0.3em] font-extrabold" style={{ color: "var(--sa)" }}>{badge}</p>}
          <h1 className={`${head} text-5xl sm:text-7xl tracking-tighter text-white mt-4 max-w-2xl leading-[1.0]`}>{headline}</h1>
          <p className="mt-5 text-white/80 max-w-md text-base sm:text-lg leading-relaxed">{subline}</p>
          <CTAs dark />
        </motion.div>
      </div>
    </section>
  );

  if (layout === "center") return (
    <section className="relative overflow-hidden py-16 sm:py-24" style={{ backgroundColor: "var(--ss)" }} data-testid="hero-center">
      <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full opacity-30 blur-3xl animate-blob" style={{ background: "var(--sa)" }} />
      <div className="absolute -bottom-24 -right-16 h-80 w-80 rounded-full opacity-25 blur-3xl animate-blob" style={{ background: "var(--sp)", animationDelay: "-6s" }} />
      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          {badge && <span className="inline-block px-4 py-1.5 rounded-full bg-white text-[11px] font-extrabold uppercase tracking-widest shadow-sm" style={{ color: "var(--sp)" }}>{badge}</span>}
          <h1 className={`${head} text-5xl sm:text-6xl tracking-tighter mt-5`} style={{ color: "var(--sp)" }}>{headline}</h1>
          <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: "var(--st)", opacity: 0.7 }}>{subline}</p>
          <div className="flex justify-center"><CTAs /></div>
        </motion.div>
      </div>
    </section>
  );

  if (layout === "bento") return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="hero-bento">
      <div className="grid lg:grid-cols-3 gap-4 auto-rows-[180px]">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
          className="lg:col-span-2 lg:row-span-2 relative rounded-2xl overflow-hidden group">
          <SafeImg src={heroSrc} alt={store.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
          <div className="absolute bottom-0 p-7 sm:p-9 text-white">
            {badge && <p className="uppercase text-[10px] tracking-[0.3em] font-extrabold text-white/70">{badge}</p>}
            <h1 className={`${head} text-4xl sm:text-5xl tracking-tighter mt-2 max-w-md`}>{headline}</h1>
            <CTAs dark />
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="rounded-2xl p-6 flex flex-col justify-between text-white" style={{ backgroundImage: "linear-gradient(135deg, var(--sa), var(--sp))" }}>
          <BadgePercent className="h-7 w-7" />
          <div>
            <p className="font-display font-extrabold text-2xl tracking-tight">Up to 40% OFF</p>
            <button onClick={secondary} className="mt-2 text-xs font-extrabold bg-white text-zinc-900 px-4 py-2 rounded-full active:scale-95 transition-transform">Shop deals</button>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="rounded-2xl overflow-hidden relative">
          <SafeImg src={products[1]?.images?.[0] || theme?.preview} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/25" />
          <p className="absolute bottom-4 left-4 text-white text-xs font-extrabold uppercase tracking-widest">New arrivals</p>
        </motion.div>
      </div>
    </section>
  );

  if (layout === "carousel") return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 grid lg:grid-cols-2 gap-10 items-center" data-testid="hero-carousel">
      <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
        {Badge}
        <h1 className={`${head} text-5xl sm:text-6xl tracking-tighter leading-[1.02] mt-5`} style={{ color: "var(--sp)" }}>{headline}</h1>
        <p className="mt-4 text-base sm:text-lg max-w-md leading-relaxed" style={{ color: "var(--st)", opacity: 0.7 }}>{subline}</p>
        <CTAs />
      </motion.div>
      <div className="relative">
        <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
          <AnimatePresence mode="wait">
            <motion.div key={slide} initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0">
              <SafeImg src={slides[slide]} alt="" className="w-full h-full object-cover" />
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex justify-center gap-2 mt-4">
          {slides.map((_, i) => (
            <button key={i} data-testid={`hero-dot-${i}`} onClick={() => setSlide(i)}
              className="h-2 rounded-full transition-all duration-300"
              style={{ width: i === slide ? 24 : 8, backgroundColor: i === slide ? "var(--sp)" : "rgba(0,0,0,0.15)" }} />
          ))}
        </div>
      </div>
    </section>
  );

  if (layout === "promo") return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="hero-promo">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="relative rounded-3xl overflow-hidden p-10 sm:p-16 text-white" style={{ backgroundImage: "linear-gradient(120deg, var(--sp), var(--sa))" }}>
        <span className="absolute -right-4 -top-12 font-display font-extrabold text-[11rem] leading-none opacity-15 select-none pointer-events-none">SALE</span>
        <div className="absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-white/10 animate-float" />
        {badge && <span className="inline-block px-4 py-1.5 rounded-full bg-white/20 backdrop-blur text-[11px] font-extrabold uppercase tracking-widest">{badge}</span>}
        <h1 className={`${head} text-5xl sm:text-7xl tracking-tighter mt-5 max-w-2xl relative`}>{headline}</h1>
        <p className="mt-4 text-white/85 max-w-md text-base sm:text-lg relative">{subline}</p>
        <div className="relative"><CTAs dark /></div>
        <div className="flex flex-wrap gap-2 mt-8 relative">
          {cats.slice(0, 5).map((c) => (
            <span key={c} className="px-3.5 py-1.5 rounded-full bg-white/15 text-xs font-bold backdrop-blur">{c}</span>
          ))}
        </div>
      </motion.div>
    </section>
  );

  if (layout === "floating") return (
    <section className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-2 items-center gap-10 overflow-hidden" data-testid="hero-floating">
      <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
        {Badge}
        <h1 className={`${head} text-6xl sm:text-7xl tracking-tighter leading-[0.98] mt-5`} style={{ color: "var(--sp)" }}>{headline}</h1>
        <p className="mt-5 text-base sm:text-lg max-w-md leading-relaxed" style={{ color: "var(--st)", opacity: 0.7 }}>{subline}</p>
        <CTAs />
        <div className="flex gap-6 mt-8">
          {(products.length > 0 ? [["Free Shipping", Truck], ["Secure Payments", ShieldCheck], ["Easy Returns", RotateCcw]] : [["Trusted & Verified", ShieldCheck], ["Premium Quality", Star], ["Fast Response", Zap]]).map(([t, Icon]) => (
            <span key={t} className="flex items-center gap-2 text-xs font-bold" style={{ color: "var(--st)", opacity: 0.65 }}>
              <Icon className="h-4 w-4" style={{ color: "var(--sa)" }} />{t}
            </span>
          ))}
        </div>
      </motion.div>
      <div className="relative flex justify-center py-8">
        <div className="absolute h-72 w-72 sm:h-96 sm:w-96 rounded-full blur-3xl opacity-40 animate-blob" style={{ background: "var(--sa)" }} />
        <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative animate-float">
          <SafeImg src={products[0]?.images?.[0] || heroSrc} alt={store.name}
            className="w-64 sm:w-80 aspect-square object-cover shadow-2xl" style={{ borderRadius: "50%", border: "8px solid white" }} />
        </motion.div>
        {products.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="absolute top-6 right-4 sm:right-14 bg-white rounded-xl shadow-xl px-4 py-3 animate-float-slow">
          <Stars rating={products[0]?.rating ?? 4.7} />
          <p className="text-[10px] font-extrabold mt-1" style={{ color: "var(--sp)" }}>{products[0]?.reviews_count ?? 500}+ happy customers</p>
        </motion.div>
        )}
        {products[0] && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
            className="absolute bottom-4 left-2 sm:left-10 bg-white rounded-xl shadow-xl px-4 py-3 animate-float-x">
            <p className="text-[10px] uppercase tracking-widest font-extrabold text-zinc-400">Bestseller</p>
            <p className="text-xs font-extrabold" style={{ color: "var(--sp)" }}>{products[0].name}</p>
          </motion.div>
        )}
      </div>
    </section>
  );

  if (layout === "editorial") return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20" data-testid="hero-editorial">
      <div className="border-t-2 pt-10" style={{ borderColor: "var(--sp)" }}>
        {badge && <p className="uppercase text-[11px] tracking-[0.35em] font-extrabold" style={{ color: "var(--sa)" }}>{badge}</p>}
        <div className="grid lg:grid-cols-12 gap-10 mt-6 items-end">
          <div className="lg:col-span-7">
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="fh text-6xl sm:text-8xl tracking-tighter leading-[0.95]" style={{ color: "var(--sp)" }}>{headline}</motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
              className="mt-6 max-w-md text-base sm:text-lg leading-relaxed" style={{ color: "var(--st)", opacity: 0.72 }}>{subline}</motion.p>
            <CTAs />
          </div>
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-5">
            <SafeImg src={heroSrc} alt={store.name} className="w-full aspect-[3/4] object-cover shadow-2xl" style={{ borderRadius: radius }} />
          </motion.div>
        </div>
      </div>
    </section>
  );

  if (layout === "asymmetric") return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-2 gap-10 items-center" data-testid="hero-asymmetric">
      <div className="order-2 lg:order-1 relative h-[400px] sm:h-[460px]">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="absolute left-0 top-0 w-2/3 aspect-[4/5] overflow-hidden shadow-xl">
          <SafeImg src={heroSrc} alt="" className="w-full h-full object-cover" style={{ borderRadius: radius }} />
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="absolute right-0 bottom-0 w-1/2 aspect-square overflow-hidden shadow-2xl animate-float"
          style={{ border: "6px solid var(--sbg)", borderRadius: radius }}>
          <SafeImg src={products[1]?.images?.[0] || theme?.preview} alt="" className="w-full h-full object-cover" />
        </motion.div>
      </div>
      <div className="order-1 lg:order-2 lg:pl-8">
        {Badge}
        <h1 className="fh text-5xl sm:text-6xl tracking-tighter leading-[1.0] mt-5" style={{ color: "var(--sp)" }}>{headline}</h1>
        <p className="mt-4 text-base sm:text-lg max-w-md leading-relaxed" style={{ color: "var(--st)", opacity: 0.72 }}>{subline}</p>
        <CTAs />
        {cats.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8">
            {cats.slice(0, 4).map((c) => (
              <span key={c} className="px-3.5 py-1.5 rounded-full text-xs font-bold" style={{ backgroundColor: "var(--ss)", color: "var(--sp)" }}>{c}</span>
            ))}
          </div>
        )}
      </div>
    </section>
  );

  /* default: split */
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16 grid lg:grid-cols-2 gap-10 items-center" data-testid="hero-split">
      <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
        {Badge}
        <h1 className={`${head} text-5xl sm:text-6xl tracking-tighter leading-[1.02] mt-5`} style={{ color: "var(--sp)" }}>{headline}</h1>
        <p className="mt-4 text-base sm:text-lg max-w-md leading-relaxed" style={{ color: "var(--st)", opacity: 0.7 }}>{subline}</p>
        <CTAs />
        <div className="flex gap-6 mt-8">
          {(products.length > 0 ? [["Free Shipping", Truck], ["Secure Payments", ShieldCheck], ["Easy Returns", RotateCcw]] : [["Trusted & Verified", ShieldCheck], ["Premium Quality", Star], ["Fast Response", Zap]]).map(([t, Icon]) => (
            <span key={t} className="flex items-center gap-2 text-xs font-bold" style={{ color: "var(--st)", opacity: 0.65 }}>
              <Icon className="h-4 w-4" style={{ color: "var(--sa)" }} />{t}
            </span>
          ))}
        </div>
      </motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
        <div className="absolute -inset-4 rounded-[2rem] opacity-25 blur-2xl" style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }} />
        <SafeImg src={heroSrc} alt={store.name} className="relative w-full aspect-[4/3] object-cover shadow-2xl" style={{ borderRadius: `calc(${radius} * 2)` }} />
        {products.length > 0 && (
        <div className="absolute -bottom-5 -left-4 bg-white rounded-xl shadow-xl px-5 py-3.5 flex items-center gap-3 animate-float">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "var(--sa)" }} />
          <span className="text-xs font-extrabold" style={{ color: "var(--sp)" }}>{products.length}+ products in store</span>
        </div>
        )}
      </motion.div>
    </section>
  );
};
